/**
 * Screens.
 *
 * Three states: signed out, choosing a character, and playing. The world only
 * exists in the third, and everything in it is driven by the Game class rather than
 * by React, because a render loop and a virtual DOM should not be arguing over who
 * owns a frame.
 */

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_GAME_SETTINGS, type GameSettings } from '../game/camera';
import { api, ApiError, storedToken, storeToken, type CharacterSummary } from '../net/api';
import { updateBeforeLogin } from '../net/updates';
import { CharacterPreview } from './CharacterPreview';
import { ColourChoice } from './Colours';
import { colourValue, DEFAULT_EYES, DEFAULT_SKIN } from './colour-values';
import { GameView } from './GameView';

type Stage = 'signed-out' | 'characters' | 'playing';

export function App(): JSX.Element {
  const [stage, setStage] = useState<Stage>(storedToken() ? 'characters' : 'signed-out');
  const [token, setToken] = useState<string | null>(storedToken());
  const [characterId, setCharacterId] = useState<string | null>(null);
  const [settings, setSettings] = useState<GameSettings>(loadSettings);

  const signOut = useCallback(() => {
    if (token) void api.logout(token).catch(() => undefined);
    storeToken(null);
    setToken(null);
    setCharacterId(null);
    setStage('signed-out');
  }, [token]);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  /**
   * Takes a newer build before anybody tries to log in with an old one.
   *
   * A stale page here is somebody who cannot get in at all, and on a phone there
   * is no hard refresh to reach for. Nothing is lost by replacing the page at
   * this point, so it is done rather than offered.
   */
  useEffect(() => {
    void updateBeforeLogin();
  }, []);

  if (stage === 'signed-out' || !token) {
    return (
      <SignIn
        onSignedIn={(next) => {
          storeToken(next);
          setToken(next);
          setStage('characters');
        }}
      />
    );
  }

  if (stage === 'characters' || !characterId) {
    return (
      <CharacterSelect
        token={token}
        onPlay={(id) => {
          setCharacterId(id);
          setStage('playing');
        }}
        onSignOut={signOut}
      />
    );
  }

  return (
    <GameView
      token={token}
      characterId={characterId}
      settings={settings}
      onSettings={setSettings}
      onLeave={() => {
        setCharacterId(null);
        setStage('characters');
      }}
      onSessionExpired={signOut}
    />
  );
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

function SignIn({ onSignedIn }: { onSignedIn: (token: string) => void }): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      const result = creating
        ? await api.register(username.trim(), password)
        : await api.login(username.trim(), password);
      onSignedIn(result.token);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <h1 className="title">Inverse</h1>
        <p className="subtitle">{creating ? 'Create an account' : 'Sign in to play'}</p>

        {error ? <p className="error">{error}</p> : null}

        <label>
          Username
          <input
            type="text"
            value={username}
            autoComplete="username"
            maxLength={12}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={password}
            autoComplete={creating ? 'new-password' : 'current-password'}
            minLength={creating ? 8 : undefined}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>

        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Working' : creating ? 'Create account' : 'Sign in'}
        </button>

        <p className="hint">
          <button
            className="link"
            type="button"
            onClick={() => {
              setCreating(!creating);
              setError(null);
            }}
          >
            {creating ? 'I already have an account' : 'I need an account'}
          </button>
        </p>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Character select
// ---------------------------------------------------------------------------

function CharacterSelect({
  token,
  onPlay,
  onSignOut,
}: {
  token: string;
  onPlay: (characterId: string) => void;
  onSignOut: () => void;
}): JSX.Element {
  const [characters, setCharacters] = useState<CharacterSummary[] | null>(null);
  const [name, setName] = useState('');
  const [look, setLook] = useState({ skin: DEFAULT_SKIN, eyes: DEFAULT_EYES });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** What is waiting on the password being typed again, if anything. */
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'reset';
    character: CharacterSummary;
  } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  /** Whether the creation screen is up, and which card is being dragged. */
  const [making, setMaking] = useState(false);
  const [dragging, setDragging] = useState(-1);
  const [password, setPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');

  const refresh = useCallback(async () => {
    try {
      const result = await api.characters(token);
      setCharacters(result.characters);
    } catch (problem) {
      if (problem instanceof ApiError && problem.status === 401) {
        onSignOut();
        return;
      }
      setError('Could not load your characters.');
      setCharacters([]);
    }
  }, [token, onSignOut]);

  useEffect(() => {
    // Loading the character list is a read from the server, not state derived from
    // props, so it belongs in an effect. Nothing is set until the request answers.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  /**
   * Reorders the list.
   *
   * Drawn immediately and sent afterwards, because a list that waits for a round
   * trip before it moves feels broken. A failed save is put back by the reload.
   */
  const reorder = async (from: number, to: number): Promise<void> => {
    setDragging(-1);
    if (!characters || from === to) return;

    const next = [...characters];
    const moved = next[from];
    if (!moved) return;

    next.splice(from, 1);
    next.splice(to, 0, moved);
    setCharacters(next);

    try {
      await api.orderCharacters(
        token,
        next.map((character) => character.id),
      );
    } catch {
      setError('Could not save that order.');
      await refresh();
    }
  };

  /** Deleting or resetting, once the password has been typed. */
  const confirm = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!confirming || busy) return;

    setBusy(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') {
        await api.deleteCharacter(token, confirming.character.id, password);
        setNotice(`${confirming.character.name} is gone.`);
      } else {
        await api.resetCharacter(token, confirming.character.id, password);
        setNotice(`${confirming.character.name} starts again.`);
      }

      setConfirming(null);
      setPassword('');
      await refresh();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      await api.changePassword(token, password, nextPassword);
      setChangingPassword(false);
      setPassword('');
      setNextPassword('');
      setNotice('Your password is changed.');
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not change it.');
    } finally {
      setBusy(false);
    }
  };

  const create = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      await api.createCharacter(token, name.trim(), {
        skin: colourValue(look.skin),
        eyes: colourValue(look.eyes),
      });
      setName('');
      setMaking(false);
      await refresh();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not create that character.');
    } finally {
      setBusy(false);
    }
  };

  if (making) {
    return (
      <div className="screen">
        <div className="panel making">
          <h1 className="title">Inverse</h1>
          <p className="subtitle">Somebody new</p>

          {error ? <p className="error">{error}</p> : null}

          <div className="making-body">
            <CharacterPreview
              skin={colourValue(look.skin)}
              eyes={colourValue(look.eyes)}
              width={180}
              height={240}
            />

            <form className="create" onSubmit={(event) => void create(event)}>
              <input
                type="text"
                value={name}
                placeholder="Their name"
                maxLength={12}
                onChange={(event) => setName(event.target.value)}
                required
              />

              <ColourChoice
                skin={look.skin}
                eyes={look.eyes}
                onChange={(which, colour) => setLook((held) => ({ ...held, [which]: colour }))}
              />

              <div className="row">
                <button type="submit" className="primary" disabled={busy || name.trim().length < 3}>
                  Create
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setMaking(false);
                    setName('');
                    setError(null);
                  }}
                >
                  Back
                </button>
              </div>
            </form>
          </div>

          <p className="hint">
            Three to twelve letters or numbers. Nothing here is final but the name.
          </p>
        </div>
      </div>
    );
  }

  const full = (characters?.length ?? 0) >= 3;

  return (
    <div className="screen">
      <div className="panel choosing">
        <h1 className="title">Inverse</h1>
        <p className="subtitle">Choose a character</p>

        {error ? <p className="error">{error}</p> : null}

        {characters === null ? (
          <p className="subtitle">Loading</p>
        ) : (
          <div className="cards">
            {characters.map((character, index) => (
              <div
                key={character.id}
                className={dragging === index ? 'card lifted' : 'card'}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const from = Number(event.dataTransfer.getData('text/plain'));
                  if (Number.isInteger(from)) void reorder(from, index);
                }}
              >
                {/* The corner is the handle: a card that could be dragged from
                    anywhere is a card that cannot have a button on it. */}
                <span
                  className="card-grip"
                  title="Drag to put them in another order"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', String(index));
                    event.dataTransfer.effectAllowed = 'move';
                    setDragging(index);
                  }}
                  onDragEnd={() => setDragging(-1)}
                >
                  ⠿
                </span>

                <CharacterPreview
                  skin={character.look?.skin ?? colourValue(DEFAULT_SKIN)}
                  eyes={character.look?.eyes ?? colourValue(DEFAULT_EYES)}
                />

                <strong className="card-name">{character.name}</strong>
                <span className="card-levels">
                  <span>Combat {character.combatLevel}</span>
                  <span>Total {character.totalLevel}</span>
                </span>

                <div className="card-buttons">
                  <button type="button" className="primary" onClick={() => onPlay(character.id)}>
                    Play
                  </button>
                  <button
                    type="button"
                    className="bin"
                    title={`Delete ${character.name}`}
                    onClick={() => setConfirming({ kind: 'delete', character })}
                  >
                    🗑
                  </button>
                </div>

                <button
                  type="button"
                  className="link card-reset"
                  onClick={() => setConfirming({ kind: 'reset', character })}
                >
                  Start again
                </button>
              </div>
            ))}

            {characters.length === 0 ? (
              <p className="subtitle" style={{ margin: 0 }}>
                Nobody yet. Make somebody.
              </p>
            ) : null}
          </div>
        )}

        {confirming ? (
          <form className="confirm" onSubmit={(event) => void confirm(event)}>
            <p className="subtitle" style={{ margin: '0 0 0.6rem' }}>
              {confirming.kind === 'delete'
                ? `Delete ${confirming.character.name}? Everything they own goes with them.`
                : `Start ${confirming.character.name} again from nothing? Their items and levels go.`}
            </p>
            <input
              type="password"
              value={password}
              placeholder="Your password"
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <div className="row">
              <button
                type="submit"
                className={confirming.kind === 'delete' ? 'danger' : ''}
                disabled={busy}
              >
                {confirming.kind === 'delete' ? 'Delete' : 'Reset'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(null);
                  setPassword('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {changingPassword ? (
          <form className="confirm" onSubmit={(event) => void submitPassword(event)}>
            <p className="subtitle" style={{ margin: '0 0 0.6rem' }}>
              Change your password
            </p>
            <input
              type="password"
              value={password}
              placeholder="Current password"
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <input
              type="password"
              value={nextPassword}
              placeholder="New password"
              minLength={8}
              onChange={(event) => setNextPassword(event.target.value)}
              required
            />
            <div className="row">
              <button type="submit" disabled={busy}>
                Change it
              </button>
              <button
                type="button"
                onClick={() => {
                  setChangingPassword(false);
                  setPassword('');
                  setNextPassword('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {notice ? <p className="notice">{notice}</p> : null}

        {/* The way to another character on one side, the account on the other.
            Three is the most anybody has, and the button says so rather than
            disappearing, which leaves somebody wondering where it went. */}
        <div className="menu-foot">
          <button
            type="button"
            className={full ? 'card-new spent' : 'card-new'}
            aria-disabled={full}
            title={full ? 'You have the maximum number of characters' : 'Make somebody new'}
            onClick={() => {
              if (!full) setMaking(true);
            }}
          >
            <span className="card-plus">+</span>
            <span>New character</span>
          </button>

          <p className="hint">
            <button
              className="link"
              type="button"
              onClick={() => {
                setChangingPassword(true);
                setConfirming(null);
                setPassword('');
              }}
            >
              Change password
            </button>
            {' \u00b7 '}
            <button className="link" type="button" onClick={onSignOut}>
              Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings persistence
// ---------------------------------------------------------------------------

const SETTINGS_KEY = 'inverse.settings';

function loadSettings(): GameSettings {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_GAME_SETTINGS };
    return { ...DEFAULT_GAME_SETTINGS, ...(JSON.parse(raw) as Partial<GameSettings>) };
  } catch {
    return { ...DEFAULT_GAME_SETTINGS };
  }
}

function saveSettings(settings: GameSettings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage is unavailable inside some embeds. Settings last for this session.
  }
}
